import { TFunction, useT } from '@transifex/react'
import { ApiProduct } from '@vend/types'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { trackUserEvent } from '@vend/utilities'
import {
  generateVariants,
  getExistingVariantCombinationDetails,
  makeProduct,
} from '@vend/business-components'
import { useCallback } from 'react'
import { AddProductFastProps } from '@vend/business-components/dist/components/AddProductFast/components/AddProductFast'
import {
  useProductsStore,
  useProductSuggestions,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useSyncManager,
  useTaxManager,
} from '../../ng-compat/hooks'
import { WEB_REGISTER } from '../web-register.constant'
import { SaleManager } from '../../managers/sale-manager'
import { RegisterManager } from '../../managers/register-manager'
import { shouldPreventExchangeForKlarna } from '../../functions/payment-types/klarna'
import {
  ReduxCurrentSale,
  selectCurrentSale,
} from '../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../util/feature-cache'
import { isReturnedESale } from '../../functions/payment-types/lspayEcom'
import { useAppSelector } from '../../state/hooks'
import { $rootScope } from '../../ng-compat/imports'
import { EVENT } from '../../constants'
import {
  convertVariantsToInitialQuickProduct,
  getExistingVariantAttributes,
  getRestorableGapVariantCombos,
  getTaxRateByGroup,
  pushNewValuesToExistingVariantAttribute,
} from '../product/utils'
import { TaxManager } from '../../managers/tax-manager'
import { SyncManager } from '../../managers/sync-manager'
import { isGiftCard } from '../../functions/lineItem'
import { selectIsFinalisingSale } from '../../state/slices/uiStateSlice'
import {
  NewVariantDetails,
  useAddProductToSaleStore,
} from './useAddProductToSaleStore'

interface EnhancedProductSuggestion {
  displayName: string
  exactMatch: boolean
  id: string
  price: string
  product?: ApiProduct
  variantCount: number
}

interface MakeQuickProductDataParams {
  product: ApiProduct
  variantProducts: ApiProduct[]
  result: NewVariantDetails
  isTaxExclusive: boolean
  taxManager: TaxManager
}

export interface ProductDetails {
  label: string | undefined
  productId: string | null
  isChild: boolean
}

const makeQuickProductData = async ({
  product,
  variantProducts,
  result,
  isTaxExclusive,
  taxManager,
}: MakeQuickProductDataParams): Promise<
  Omit<AddProductFastProps, 'onClose' | 'onBack'>
> => {
  const existingVariantAttributes = getExistingVariantAttributes(
    product,
    variantProducts
  )

  const variantDefinition = {
    internalID: result.newVariantAttribute.internalID,
    attribute: result.newVariantAttribute.attribute,
    values: result.newVariantAttribute.values,
  }

  const newVariantAttributes = pushNewValuesToExistingVariantAttribute(
    existingVariantAttributes,
    variantDefinition
  )

  const variantTaxRateMap: { [id: string]: number } = {}

  if (!isTaxExclusive) {
    // Get each variant's tax group if it is tax inclusive store
    const variantTaxGroups = await Promise.all(
      variantProducts.map(v => taxManager.getTaxForProduct(v.id))
    )
    variantProducts.forEach((v, index) => {
      const taxGroup = variantTaxGroups[index].tax
      variantTaxRateMap[v.id] = getTaxRateByGroup(taxGroup)
    })
  }

  const prod = convertVariantsToInitialQuickProduct(
    product,
    variantProducts,
    isTaxExclusive,
    variantTaxRateMap,
    newVariantAttributes
  )

  const restorableGapVariantCombinations = getRestorableGapVariantCombos(
    existingVariantAttributes,
    variantDefinition,
    result.selectedAttributeValues
  )

  const qproduct = makeProduct(prod)

  const existingVariantCombinationDetails = getExistingVariantCombinationDetails(
    qproduct,
    existingVariantAttributes,
    restorableGapVariantCombinations
  )

  const qproductWithVariants = await generateVariants(
    qproduct,
    existingVariantCombinationDetails
  )

  return {
    sourcePage: 'quickkeys',
    initialProduct: qproductWithVariants,
    types: ['Variant'],
    redirectToEap: false,
    existingVariantAttributes,
    existingVariantCombinationDetails,
  }
}

const validate = (
  product: ApiProduct | undefined,
  displayName: string,
  variantCount: number,
  exactMatch: boolean,
  currentSale: ReduxCurrentSale,
  t: TFunction,
  saleManager: SaleManager,
  registerManager: RegisterManager
): product is ApiProduct => {
  if (!product) {
    const message = t(
      `"{label}" has been deleted and can't be sold. Please select another product.`,
      { label: displayName, _context: 'Quick key product not found' }
    )
    ToastNotifications.negative(message)
    return false
  }

  if (!product.active) {
    const message = t(
      `"{label}" has been deactivated and can't be sold. Please select another product.`,
      {
        label: displayName,
        _context: 'Quick key product is deactivated',
      }
    )
    // eslint-disable-next-line no-console
    console.warn(message)
    ToastNotifications.negative(message)
    return false
  }

  // Mitigate against a performance bug where a retailer can select a product but transition to the sale
  // confirmation view before it is added to the sale.
  if (registerManager.isActiveRegisterOpen() && saleManager.isInitialised) {
    saleManager.saleCalculating = true
  }

  trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Quick Key Selected', {
    'Has Variants': variantCount > 0,
    'Exact Match': exactMatch,
    'View Product Info': !registerManager.isActiveRegisterOpen(),
  })

  if (shouldPreventExchangeForKlarna(currentSale)) {
    saleManager.saleCalculating = false
    ToastNotifications.negative(
      t(
        'Unable to exchange items for eCom sales paid with Lightspeed Payments.',
        { _context: 'Ecom return not allowed error message' }
      )
    )
    return false
  }

  if (
    getFeatureFromCache('lspay_eseries_refunds_on_xseries') &&
    isReturnedESale(currentSale)
  ) {
    saleManager.saleCalculating = false
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Attempt to exchange an lspay eCom sale',
      {
        saleId: currentSale.id,
      }
    )
    ToastNotifications.negative(
      t('You cannot add items to a returned sale. Please start a new sale.', {
        _context: 'Returned sale error message',
      })
    )
    return false
  }

  return true
}

const addProductsToSale = (products: ApiProduct[]) => {
  products.forEach(product => {
    $rootScope.$broadcast(EVENT.SEARCH_PRODUCT_SELECTED, product)
  })
}

const syncProducts = (syncManager: SyncManager) => {
  let resolveSync: (value: unknown) => void
  const syncPromise = new Promise(resolve => {
    resolveSync = resolve
  })

  $rootScope.$apply(() => {
    syncManager.sync().then(resolveSync)
  })

  return syncPromise
}

const isNewVariantDetails = (
  product: ApiProduct | ApiProduct[] | NewVariantDetails
): product is NewVariantDetails => {
  return (product as NewVariantDetails).hasNewVariantAttribute !== undefined
}

export const useAddProductToSale = () => {
  const {
    setSelectVariant,
    addNewVariantToSelectProps,
    setAddProduct,
    openGiftCardModal,
    closeGiftCardModal,
    state,
  } = useAddProductToSaleStore()
  const productSuggestions = useProductSuggestions()
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const currentSale = useAppSelector(selectCurrentSale)
  const productsStore = useProductsStore()
  const retailerSettingsManager = useRetailerSettingsManager()
  const taxManager = useTaxManager()
  const syncManager = useSyncManager()
  const isFinalisingSale = useAppSelector(selectIsFinalisingSale)
  const t = useT()

  const retailer = retailerSettingsManager.get()

  const syncAndAddProductsToSale = useCallback(
    async (productId: string, variantIds: string[] | undefined) => {
      // Sync the newly created products to IndexedDB
      await syncProducts(syncManager)

      // Fetch the new products from IndexedDB
      const products = await Promise.all(
        [productId, ...(variantIds || [])].map(id => productsStore.get(id))
      )
      const filteredProducts = products.filter((p): p is ApiProduct =>
        Boolean(p)
      )
      addProductsToSale(filteredProducts)
      setSelectVariant(null)
      setAddProduct(null)
    },
    [productsStore, setAddProduct, setSelectVariant, syncManager]
  )

  const openProductModal = useCallback(
    async (
      product: ApiProduct,
      variantProducts: ApiProduct[],
      result: NewVariantDetails
    ) => {
      const data = await makeQuickProductData({
        product,
        variantProducts,
        result,
        isTaxExclusive: retailer ? retailer.tax_exclusive : false,
        taxManager,
      })
      const onClose = async (
        productId: string | undefined,
        variantIds: string[] | undefined
      ) => {
        if (!productId) {
          setSelectVariant(null)
          setAddProduct(null)
          return
        }

        syncAndAddProductsToSale(productId, variantIds)
      }

      const onBack = () => {
        setAddProduct(null)
      }

      setAddProduct({
        ...data,
        onClose,
        onBack,
      })
    },
    [
      retailer,
      setAddProduct,
      setSelectVariant,
      syncAndAddProductsToSale,
      taxManager,
    ]
  )

  const openVariantModal = useCallback(
    async (product: ApiProduct) => {
      const variantProducts = await productsStore.getAllVariantProducts(product)

      const close = async (
        variantPromise: Promise<
          ApiProduct | ApiProduct[] | NewVariantDetails | undefined
        >
      ) => {
        const result = await variantPromise

        if (!result) {
          setSelectVariant(null)
          return
        }

        if (isNewVariantDetails(result)) {
          openProductModal(product, variantProducts, result)
          // Add the new variant attribute to the config in case they use back button
          addNewVariantToSelectProps(result)
          return
        }
        setSelectVariant(null)
        addProductsToSale(Array.isArray(result) ? result : [result])
      }

      setSelectVariant({
        close,
        parentProduct: product,
        variantProducts,
        allowParentSelect: false,
        showAddValue: true,
      })
    },
    [
      addNewVariantToSelectProps,
      openProductModal,
      productsStore,
      setSelectVariant,
    ]
  )

  const addProductToSale = useCallback(
    async (details: ProductDetails) => {
      const result = (await productSuggestions.enhance({
        id: details.productId,
        exactMatch: details.isChild,
      })) as EnhancedProductSuggestion

      if (
        !validate(
          result.product,
          result.displayName,
          result.variantCount,
          result.exactMatch,
          currentSale,
          t,
          saleManager,
          registerManager
        )
      ) {
        return
      }

      // eslint-disable-next-line no-console
      console.debug('[Global Search] Product result selected', result)

      if (
        getFeatureFromCache('react_gift_card_modals') &&
        isGiftCard(result.product) &&
        registerManager.isActiveRegisterOpen() &&
        !isFinalisingSale
      ) {
        openGiftCardModal()
        return
      }

      if (!result.variantCount || result.exactMatch) {
        addProductsToSale([result.product])
        return
      }

      openVariantModal(result.product)
    },
    [
      currentSale,
      isFinalisingSale,
      openGiftCardModal,
      openVariantModal,
      productSuggestions,
      registerManager,
      saleManager,
      t,
    ]
  )

  const createNewProductBySearchTerm = useCallback(
    (newProductName: string) => {
      const onClose = () => {
        setAddProduct(null)
      }

      const onBack = () => {
        setAddProduct(null)
      }

      const afterSave = async (
        productId?: string,
        variantIds?: string[],
        _lspcProductIds?: string[],
        productName?: string
      ) => {
        if (!productId) {
          setAddProduct(null)
          return
        }

        try {
          await syncAndAddProductsToSale(productId, variantIds)

          ToastNotifications.success(
            t('{productName} added', {
              _context: 'Toast Notification',
              productName,
            })
          )
        } catch {
          let errorToastMessage = t(
            'We’re unable to add the created product to the sale. Try again.',
            {
              _context:
                'Toast Notification when failed to add newly created product',
            }
          )
          if (productName) {
            errorToastMessage = t(
              'We’re unable to add the created product {productName} to the sale. Try again.',
              {
                _context:
                  'Toast Notification when failed to add newly created product',
                productName,
              }
            )
          }
          ToastNotifications.negative(errorToastMessage)
        }

        setAddProduct(null)
      }

      setAddProduct({
        initialProduct: {
          name: newProductName,
        },
        sourcePage: 'ProductSearch',
        types: ['Standard', 'Variant'],
        redirectToEap: false,
        afterSave,
        onClose,
        onBack,
      })
    },
    [setAddProduct, syncAndAddProductsToSale, t]
  )

  return {
    addProductToSale,
    createNewProductBySearchTerm,
    addProductOptions: state.addProductFastProps,
    variantPickerOptions: state.variantPickerProps,
    showGiftCardModal: state.showGiftCardModal,
    closeGiftCardModal,
  }
}
